import type { CSSProperties } from 'react';
import { groupDigits } from '../../rounds/review-model';

/** One ranked participant in a graded round. The composition root derives names and
 * placements from the standings read; this component only renders that already-checked view. */
export interface GradedColony {
  colony: number;
  displayName: string | null;
  /** G1 — living bees at the end of the graded round, or (spectate) at the playhead. */
  score: number;
  /** Shared placements deliberately share this ordinal. Meaningless in `orderBy: 'score'`
   * mode — spectate renders no placement digit at all, only the rank the row's position
   * already states (`client-rounds-review`, "the rail lists the top N colonies by score"). */
  placement: number;
  /** No member stands behind this colony — a bot from the roster fill
   * (`tournament-big-screen` **D8**). Rendered as a bot rather than an invented player name. */
  isBot?: boolean;
}

export interface ColonyRailProps {
  colonies: readonly GradedColony[];
  subjectColony: number;
  onSelect(colony: number): void;
  /**
   * Bounds the rendered row count to the top `rowCap` entries after ordering
   * (`client-rounds-review`, the spectate rail: "never renders one row per colony").
   * `undefined` (review's default) renders every entry, unbounded.
   */
  rowCap?: number;
  /**
   * `'placement'` (default) — the graded round's final ranking, unchanged from how review has
   * always ordered it. `'score'` — descending by {@link GradedColony.score} as it stands right
   * now, tie-broken by colony id, for a spectate rail whose order must track the playhead.
   */
  orderBy?: 'placement' | 'score';
}

function ordinal(value: number): string {
  const tail = value % 100;
  if (tail >= 11 && tail <= 13) return `${value}th`;
  switch (value % 10) {
    case 1: return `${value}st`;
    case 2: return `${value}nd`;
    case 3: return `${value}rd`;
    default: return `${value}th`;
  }
}

function ordered(
  colonies: readonly GradedColony[],
  orderBy: 'placement' | 'score',
  rowCap?: number,
): readonly GradedColony[] {
  const sorted = [...colonies].sort((a, b) =>
    orderBy === 'score'
      ? b.score - a.score || a.colony - b.colony
      : a.placement - b.placement || a.colony - b.colony,
  );
  return rowCap === undefined ? sorted : sorted.slice(0, rowCap);
}

const ROW_STYLE: CSSProperties = {
  minHeight: 44,
  display: 'grid',
  gridTemplateColumns: '1fr auto auto',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  padding: '7px 10px',
  background: 'var(--hud-bg)',
  border: '1.5px solid var(--hud-line)',
  borderRadius: 12,
  color: 'var(--hud-text)',
  cursor: 'pointer',
  fontFamily: 'var(--font-display)',
  textAlign: 'left',
};

/** The ranked, read-only subject selector for the graded review route. It has no transport
 * seam: a click can only change the local watched-colony state the parent owns. */
export function ColonyRail({
  colonies,
  subjectColony,
  onSelect,
  rowCap,
  orderBy = 'placement',
}: ColonyRailProps) {
  if (colonies.length === 0) return null;
  return (
    <aside data-testid="graded-colony-rail" aria-label="Colonies in this graded round" style={{ position: 'absolute', left: 12, top: 100, width: 232, display: 'grid', gap: 5 }}>
      <h4 style={{ margin: '0 0 2px', paddingLeft: 2, color: 'var(--hud-text-dim)', fontSize: 9, letterSpacing: 'var(--tracking-caps)', textTransform: 'uppercase' }}>
        Colonies
      </h4>
      {ordered(colonies, orderBy, rowCap).map((entry) => {
        const active = entry.colony === subjectColony;
        return (
          <button
            key={entry.colony}
            type="button"
            data-testid={`graded-colony-${entry.colony}`}
            aria-pressed={active}
            onClick={() => onSelect(entry.colony)}
            style={{ ...ROW_STYLE, borderColor: active ? 'var(--accent)' : 'var(--hud-line)', background: active ? 'var(--accent-soft)' : 'var(--hud-bg)' }}
          >
            <span>
              <b>Colony {entry.colony}</b>
              {entry.isBot === true ? ' · Bot' : entry.displayName === null ? '' : ` · ${entry.displayName}`}
            </span>
            <span style={{ color: 'var(--hud-text-dim)', fontVariantNumeric: 'tabular-nums' }}>{groupDigits(entry.score)} G1</span>
            {orderBy === 'placement' && (
              <span style={{ color: active ? 'var(--accent-strong)' : 'var(--hud-text)', fontWeight: 700 }}>{ordinal(entry.placement)}</span>
            )}
          </button>
        );
      })}
    </aside>
  );
}

/** Compact second-control-bar selector. It intentionally delegates selection to the same local
 * callback as the rail, so it cannot become a second trace-opening path. */
export function ColonyChipBar({ colonies, subjectColony, onSelect }: ColonyRailProps) {
  if (colonies.length === 0) return null;
  return (
    <div data-testid="graded-colony-chip-bar" style={{ position: 'absolute', left: 12, right: 12, bottom: 72, display: 'flex', gap: 6, overflowX: 'auto', padding: '2px 0' }}>
      {ordered(colonies, 'placement').map((entry) => (
        <button
          key={entry.colony}
          type="button"
          aria-pressed={entry.colony === subjectColony}
          onClick={() => onSelect(entry.colony)}
          style={{ minHeight: 44, padding: '0 12px', borderRadius: 999, border: `1.5px solid ${entry.colony === subjectColony ? 'var(--accent)' : 'var(--hud-line)'}`, background: entry.colony === subjectColony ? 'var(--accent-soft)' : 'var(--hud-bg)', color: 'var(--hud-text)', fontFamily: 'var(--font-display)', cursor: 'pointer', whiteSpace: 'nowrap' }}
        >
          C{entry.colony} · {ordinal(entry.placement)}
        </button>
      ))}
    </div>
  );
}
